// Shared HTTP client — handles auth token, error extraction, 401 handling
import { API_BASE, STORAGE_KEYS } from '../../config/env';
import { isTokenExpired } from '../utils/jwt';

function getToken() {
  try {
    const item = localStorage.getItem(STORAGE_KEYS.auth) || sessionStorage.getItem(STORAGE_KEYS.auth);
    return item ? JSON.parse(item)?.token || null : null;
  } catch {
    return null;
  }
}

// Single-flight session expiry: only the request that was sent with the token
// still in storage may clear it. Once cleared, getToken() is null (or a newer
// token), so concurrent/stale 401s become no-ops and cannot wipe a new login.
function expireSession(usedToken) {
  if (getToken() !== usedToken) return;
  localStorage.removeItem(STORAGE_KEYS.auth);
  sessionStorage.removeItem(STORAGE_KEYS.auth);
  window.dispatchEvent(new Event('ism:unauthorized'));
}

function sessionError(code) {
  const error = new Error('Session expired. Please sign in again.');
  error.status = 401;
  error.code = code;
  return error;
}

async function extractErrorMessage(res) {
  const raw = await res.text();
  let message = raw || `API error ${res.status}`;
  try {
    const data = JSON.parse(raw);
    if (typeof data.error?.message === 'string') {
      message = data.error.message;
    } else if (typeof data.detail === 'string') {
      message = data.detail;
    } else if (Array.isArray(data.detail) && data.detail.length) {
      message = data.detail.map(d => d.msg || JSON.stringify(d)).join('; ');
    } else if (data.detail) {
      message = JSON.stringify(data.detail);
    }
  } catch {
    // Not JSON — keep raw text.
  }
  return message.replace(/^Value error,\s*/i, '');
}

async function performRequest(url, options = {}) {
  // Every read/write goes through this client so session expiry, cancellation
  // and non-2xx errors follow one consistent contract. Refresh logic lives in
  // the hook that owns the dashboard state, not in this low-level transport.
  const token = getToken();
  if (!token) {
    // Never send a protected request without credentials (the backend would
    // answer 403) and never signal expiry: there is no session to expire.
    throw sessionError('NO_TOKEN');
  }
  if (isTokenExpired(token)) {
    expireSession(token);
    throw sessionError('TOKEN_EXPIRED');
  }
  const headers = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {}),
  };
  headers['Authorization'] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeout || 15000);
  const externalSignal = options.signal;
  const abortFromExternalSignal = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', abortFromExternalSignal, { once: true });
  }

  let res;
  try {
    res = await fetch(url, { ...options, headers, signal: controller.signal });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Request was cancelled or timed out.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', abortFromExternalSignal);
  }

  if (res.status === 401) {
    // Token expired or invalid — clear storage and notify the app once, and
    // only if this request's token is still the stored one.
    expireSession(token);
    throw sessionError('UNAUTHORIZED');
  }

  if (!res.ok && res.status !== 304) {
    const message = await extractErrorMessage(res);
    const error = new Error(message);
    error.status = res.status;
    error.url = url;
    throw error;
  }
  return res;
}

export async function apiFetch(url, options = {}) {
  const res = await performRequest(url, options);
  if (res.status === 304) return null;
  if (res.status === 204) return null;
  if (res.headers.get('content-length') === '0') return null;
  return res.json();
}

// Opt-in variant for endpoints whose response headers carry data the caller
// needs (for example `/api/tickets/since`'s `X-Next-Cursor` on a bodyless
// 204). Existing `apiFetch` callers are unaffected.
export async function apiFetchWithMeta(url, options = {}) {
  const res = await performRequest(url, options);
  let data = null;
  if (res.status !== 204 && res.status !== 304 && res.headers.get('content-length') !== '0') {
    data = await res.json();
  }
  return { status: res.status, headers: res.headers, data };
}

export { API_BASE };
